import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const content = {
  en: {
    title: "Terms of Service",
    back: "Back to Home",
    sections: [
      {
        heading: "1. Acceptance of Terms",
        body: 'By accessing and using the SolVida Financial website ("Website"), you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our Website or services.'
      },
      {
        heading: "2. Services Provided",
        body: "SolVida Financial provides life insurance brokerage services, including but not limited to:",
        list: ["Term Life Insurance", "Mortgage Protection Insurance", "Final Expense Insurance", "Indexed Universal Life Insurance"],
        after: "We act as an independent insurance agent and represent multiple insurance carriers."
      },
      {
        heading: "3. No Guarantee of Coverage",
        body: "Submission of an inquiry or application through our Website does not guarantee insurance coverage. All insurance policies are subject to underwriting approval by the insurance carrier. Coverage terms, conditions, and availability may vary based on individual circumstances."
      },
      {
        heading: "4. Accuracy of Information",
        body: "While we strive to provide accurate and up-to-date information, we make no representations or warranties regarding the completeness, accuracy, or reliability of any information on this Website. Insurance products, rates, and terms are subject to change without notice."
      },
      {
        heading: "5. User Responsibilities",
        body: "You agree to:",
        list: ["Provide accurate and complete information when requesting quotes or services", "Not use our Website for any unlawful purpose", "Not attempt to gain unauthorized access to our systems", "Not transmit any harmful code or malware", "Comply with all applicable laws and regulations"]
      },
      {
        heading: "6. Intellectual Property",
        body: "All content on this Website, including text, graphics, logos, images, and software, is the property of SolVida Financial or its content suppliers and is protected by copyright and other intellectual property laws. You may not reproduce, distribute, or create derivative works without our express written permission."
      },
      {
        heading: "7. Third-Party Links",
        body: "Our Website may contain links to third-party websites. We are not responsible for the content, privacy practices, or terms of service of any third-party sites. Your use of third-party websites is at your own risk."
      },
      {
        heading: "8. Limitation of Liability",
        body: "To the fullest extent permitted by law, SolVida Financial shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or relating to your use of our Website or services. Our total liability shall not exceed the amount of commissions received from your insurance policy, if any."
      },
      {
        heading: "9. Disclaimer of Warranties",
        body: 'Our Website and services are provided "as is" and "as available" without any warranties of any kind, either express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement.'
      },
      {
        heading: "10. Indemnification",
        body: "You agree to indemnify and hold harmless SolVida Financial, its officers, employees, and agents from any claims, damages, losses, or expenses arising from your use of our Website or violation of these Terms."
      },
      {
        heading: "11. Termination",
        body: "We reserve the right to terminate or suspend your access to our Website at any time, without notice, for conduct that we believe violates these Terms or is harmful to other users, us, or third parties."
      },
      {
        heading: "12. Governing Law",
        body: "These Terms shall be governed by and construed in accordance with the laws of the State of Georgia, without regard to its conflict of law provisions. Any disputes shall be resolved in the courts located in Georgia."
      },
      {
        heading: "13. Changes to Terms",
        body: "We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to the Website. Your continued use of the Website after changes constitutes acceptance of the modified Terms."
      },
      {
        heading: "14. Contact Information",
        body: "For questions about these Terms of Service, please contact us:"
      }
    ]
  },
  es: {
    title: "Términos de Servicio",
    back: "Volver al Inicio",
    sections: [
      {
        heading: "1. Aceptación de los Términos",
        body: 'Al acceder y utilizar el sitio web de SolVida Financial ("Sitio Web"), usted acepta y se compromete a cumplir con estos Términos de Servicio. Si no está de acuerdo con estos términos, por favor no utilice nuestro Sitio Web ni nuestros servicios.'
      },
      {
        heading: "2. Servicios Proporcionados",
        body: "SolVida Financial ofrece servicios de corretaje de seguros de vida, incluyendo pero no limitado a:",
        list: ["Seguro de Vida a Término", "Seguro de Protección Hipotecaria", "Seguro de Gastos Finales", "Seguro de Vida Universal Indexado"],
        after: "Actuamos como agentes de seguros independientes y representamos a múltiples compañías aseguradoras."
      },
      {
        heading: "3. Sin Garantía de Cobertura",
        body: "La presentación de una consulta o solicitud a través de nuestro Sitio Web no garantiza la cobertura de seguros. Todas las pólizas de seguros están sujetas a la aprobación de suscripción por parte de la compañía aseguradora. Los términos, condiciones y disponibilidad de la cobertura pueden variar según las circunstancias individuales."
      },
      {
        heading: "4. Exactitud de la Información",
        body: "Si bien nos esforzamos por proporcionar información precisa y actualizada, no hacemos representaciones ni garantías con respecto a la integridad, exactitud o confiabilidad de cualquier información en este Sitio Web. Los productos de seguros, las tarifas y los términos están sujetos a cambios sin previo aviso."
      },
      {
        heading: "5. Responsabilidades del Usuario",
        body: "Usted se compromete a:",
        list: ["Proporcionar información precisa y completa al solicitar cotizaciones o servicios", "No utilizar nuestro Sitio Web para ningún propósito ilegal", "No intentar obtener acceso no autorizado a nuestros sistemas", "No transmitir ningún código dañino o malware", "Cumplir con todas las leyes y regulaciones aplicables"]
      },
      {
        heading: "6. Propiedad Intelectual",
        body: "Todo el contenido de este Sitio Web, incluyendo texto, gráficos, logotipos, imágenes y software, es propiedad de SolVida Financial o de sus proveedores de contenido y está protegido por las leyes de derechos de autor y otras leyes de propiedad intelectual. No puede reproducir, distribuir ni crear obras derivadas sin nuestro permiso expreso por escrito."
      },
      {
        heading: "7. Enlaces de Terceros",
        body: "Nuestro Sitio Web puede contener enlaces a sitios web de terceros. No somos responsables del contenido, las prácticas de privacidad ni los términos de servicio de ningún sitio de terceros. Su uso de sitios web de terceros es bajo su propio riesgo."
      },
      {
        heading: "8. Limitación de Responsabilidad",
        body: "En la máxima medida permitida por la ley, SolVida Financial no será responsable de ningún daño indirecto, incidental, especial, consecuente o punitivo que surja de o esté relacionado con su uso de nuestro Sitio Web o servicios. Nuestra responsabilidad total no excederá el monto de las comisiones recibidas de su póliza de seguros, si las hubiere."
      },
      {
        heading: "9. Renuncia de Garantías",
        body: 'Nuestro Sitio Web y servicios se proporcionan "tal cual" y "según disponibilidad" sin garantías de ningún tipo, ya sean expresas o implícitas, incluyendo pero sin limitarse a garantías de comerciabilidad, idoneidad para un propósito particular o no infracción.'
      },
      {
        heading: "10. Indemnización",
        body: "Usted acepta indemnizar y mantener indemne a SolVida Financial, sus directivos, empleados y agentes de cualquier reclamo, daño, pérdida o gasto que surja de su uso de nuestro Sitio Web o la violación de estos Términos."
      },
      {
        heading: "11. Terminación",
        body: "Nos reservamos el derecho de terminar o suspender su acceso a nuestro Sitio Web en cualquier momento, sin previo aviso, por conducta que creamos que viola estos Términos o que es perjudicial para otros usuarios, para nosotros o para terceros."
      },
      {
        heading: "12. Ley Aplicable",
        body: "Estos Términos se regirán e interpretarán de acuerdo con las leyes del Estado de Georgia, sin tener en cuenta sus disposiciones sobre conflictos de leyes. Cualquier disputa se resolverá en los tribunales ubicados en Georgia."
      },
      {
        heading: "13. Cambios en los Términos",
        body: "Nos reservamos el derecho de modificar estos Términos en cualquier momento. Los cambios serán efectivos inmediatamente después de su publicación en el Sitio Web. Su uso continuado del Sitio Web después de los cambios constituye la aceptación de los Términos modificados."
      },
      {
        heading: "14. Información de Contacto",
        body: "Para preguntas sobre estos Términos de Servicio, por favor contáctenos:"
      }
    ]
  }
};

const TermsOfService = () => {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const c = content[lang] || content.en;
  const dateFmt = lang === 'es' ? 'es-CO' : 'en-US';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-20 px-4 sm:px-6 lg:px-8">
      <div className="container mx-auto max-w-4xl">
        <Button onClick={() => navigate('/')} variant="outline" className="mb-8 flex items-center gap-2" data-testid="terms-back-btn">
          <ArrowLeft size={20} />
          {c.back}
        </Button>

        <div className="bg-white rounded-lg shadow-xl p-8 md:p-12">
          <h1 className="text-4xl font-bold text-slate-900 mb-6" data-testid="terms-title">{c.title}</h1>
          <p className="text-sm text-slate-600 mb-8">
            {lang === 'es' ? 'Fecha de vigencia: ' : 'Effective Date: '}
            {new Date().toLocaleDateString(dateFmt, { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>

          <div className="prose prose-slate max-w-none space-y-6">
            {c.sections.map((s) => (
              <section key={s.heading}>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">{s.heading}</h2>
                <p className="text-slate-700 leading-relaxed mb-4">{s.body}</p>
                {s.list && (
                  <ul className="list-disc pl-6 space-y-2 text-slate-700">
                    {s.list.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                )}
                {s.after && <p className="text-slate-700 leading-relaxed mt-4">{s.after}</p>}
              </section>
            ))}
            <div className="bg-slate-50 p-6 rounded-lg">
              <p className="text-slate-700"><strong>SolVida Financial</strong></p>
              <p className="text-slate-700">Email: ivonne@solvidafinancial.com</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
